import { Tooltip } from 'antd';
import {
  RiskSymbolCell,
  TagsCell,
  TreatmentProductsCell,
  ActiveSubstancesCell,
  SimpleTextCell,
  CombinedDelayCell,
  ActivityTypesCell,
  EnemiesCell,
  EnemiesAndActivityTypesCell,
  ProductWithDoseCell,
  ApprovalNumberCell,
  ApprovalStatusCell,
  ActionLinkCell,
} from '@client/components/ui/table/TableCells';
import RiskIndexDisplay from '@client/components/ui/RiskIndexDisplay';
import TruncatedText from '@client/components/ui/TruncatedText';
import ItemsWithPopover from '@client/components/ui/table/ItemWithPopover';
import IriisLink from '@client/components/ui/IriisLink';
import { getArlaRatingStyle } from '@client/utils/arlaRating';
import { getTreatmentBadges } from '@client/utils/treatmentValidity';

export const createExpandedRowRender = () => (record) => {
  const uniqueItems = new Map();

  const addUnique = (id, name) => {
    if (name && !uniqueItems.has(id)) {
      uniqueItems.set(id, name);
    }
  };

  record.treatment_cultures?.forEach((treatmentCulture) => {
    if (treatmentCulture.culture) {
      addUnique(
        treatmentCulture.culture.id,
        treatmentCulture.culture.french_name
      );
    }
    treatmentCulture.enemies?.forEach((enemy) => {
      addUnique(
        enemy.id,
        enemy.client_display_name || enemy.french_name || enemy.display_name
      );
    });
  });

  if (uniqueItems.size === 0) return null;

  const tagGroups = Array.from(uniqueItems.entries())
    .slice(0, 20)
    .map(([id, name]) => ({
      key: id,
      name: name,
    }));

  return (
    <div className="expanded-row">
      <TagsCell
        items={tagGroups}
        getItemName={(item) => item.name}
        getItemKey={(item) => item.key}
      />
    </div>
  );
};

/**
 * Weed group definitions for herbicide columns.
 * Each group maps to a backend weed_group label on the enemy model.
 */
const WEED_GROUPS = [
  {
    key: 'annual_grasses',
    labelKey: 'table.weed_group_annual_grasses',
    matchLabels: ['Graminées annuelles'],
  },
  {
    key: 'annual_broadleaf',
    labelKey: 'table.weed_group_annual_broadleaf',
    matchLabels: ['Feuilles larges annuelles'],
  },
  {
    key: 'perennial_grasses',
    labelKey: 'table.weed_group_perennial_grasses',
    matchLabels: ['Graminées vivaces'],
  },
  {
    key: 'perennial_broadleaf',
    labelKey: 'table.weed_group_perennial_broadleaf',
    matchLabels: ['Feuilles larges vivaces'],
  },
  {
    key: 'perennial',
    labelKey: 'table.weed_group_perennial',
    matchLabels: ['Vivaces'],
  },
];

export const createTableColumns = (
  t,
  currentSorting,
  searchData,
  filters,
  searchMode = 'rapide',
  iriisLinks = null,
  detailQuerySuffix = ''
) => {
  // Get treatment type label for dynamic column header
  const treatmentTypeLabel =
    searchData?.selectionLabels?.treatment_type?.[0]?.label?.toLowerCase() ||
    '';

  // Detect treatment type categories for conditional column display
  const isDefanant = treatmentTypeLabel.includes('défanant');
  const isHerbicide =
    treatmentTypeLabel.includes('herbicide') || isDefanant;
  const isFongicide = treatmentTypeLabel.includes('fongicide');

  // Collect filtered enemy IDs (from step selections or extra filters)
  const filteredEnemyIds = [
    ...(searchData?.selections?.['treatment_enemies.enemy'] || []),
    ...(filters?.enemies || []),
  ];
  const hasEnemiesFilter = filteredEnemyIds.length > 0;

  // --- Multichoix dynamic columns ---

  const selectedCultures =
    searchData?.selectionLabels?.cultures ||
    searchData?.selectionLabels?.['treatment_cultures.culture'] ||
    [];
  const cultureColumns =
    searchMode === 'multichoix' && selectedCultures.length > 1
      ? [...selectedCultures]
          .sort((a, b) => a.label.localeCompare(b.label))
          .map((culture) => ({
            title: (
              <Tooltip title={culture.label}>
                <div className="vertical-header-text">
                  {culture.label.length > 20 ? `${culture.label.slice(0, 20)}...` : culture.label}
                </div>
              </Tooltip>
            ),
            key: `culture_${culture.value}`,
            className: 'vertical-cell',
            width: 50,
            align: 'center',
            render: (_, record) => {
              const hasCulture = record.treatment_cultures?.some(
                (tc) => tc.culture?.id === culture.value
              );
              return hasCulture ? (
                <span className="text-green-600 font-bold">&#10003;</span>
              ) : (
                <span>-</span>
              );
            },
          }))
      : [];

  const selectedEnemies =
    searchData?.selectionLabels?.['treatment_enemies.enemy'] || [];
  const enemyColumns =
    searchMode === 'multichoix' && selectedEnemies.length > 0
      ? [...selectedEnemies]
          .sort((a, b) => a.label.localeCompare(b.label))
          .map((enemy) => ({
            title: (
              <Tooltip title={enemy.label}>
                <div className="vertical-header-text">
                  <IriisLink url={iriisLinks?.[enemy.value]?.url}>
                    {enemy.label.length > 20 ? `${enemy.label.slice(0, 20)}...` : enemy.label}
                  </IriisLink>
                </div>
              </Tooltip>
            ),
            key: `enemy_${enemy.value}`,
            className: 'vertical-cell',
            width: 60,
            render: (_, record) => {
              const treatmentEnemy = record.treatment_enemies?.find(
                (te) => te.enemy?.id === enemy.value
              );
              const rating = treatmentEnemy?.enemy_rating_ARLA;
              if (rating?.name) {
                return (
                  <span
                    className="inline-flex flex-col justify-center items-center rounded-sm text-xs"
                    style={{
                      padding: '4px 8px',
                      ...getArlaRatingStyle(rating.display_color),
                    }}
                  >
                    {rating.name}
                  </span>
                );
              }
              return (
                <span className="text-gray-400 text-xs italic font-light">
                  N/A
                </span>
              );
            },
          }))
      : [];

  // Determine which header to use based on treatment type
  const getActivityTypesTitle = () => {
    if (isFongicide) {
      return t('treatment-search|table.preventive_curative');
    }
    if (treatmentTypeLabel.includes('insecticide')) {
      return t('treatment-search|table.activity_types_insecticide');
    }
    return t('treatment-search|table.activity_types_generic');
  };

  // --- Column definitions ---

  const productsColumn = {
    title: (
      <span className="text-[10px]">
        {t('treatment-search|table.commercial_products')}
      </span>
    ),
    dataIndex: 'name',
    key: 'commercial_products',
    render: (_, record) => {
      const comment =
        record?.agronomic_prescription_comments?.comment ||
        record?.agronomic_prescription_comments?.name;
      return (
        <TreatmentProductsCell
          TreatmentProducts={record?.treatment_products}
          showPrescription={record?.agronomic_prescription_icon_be_displayed_on_client_site}
          prescriptionComments={typeof comment === 'string' ? comment : ''}
        />
      );
    },
    sorter: true,
    sortOrder: currentSorting?.field === 'name' ? currentSorting.order : null,
    width: 200,
    onHeaderCell: () => ({
      style: {
        height: 'auto',
        verticalAlign: 'middle',
        padding: '8px',
      },
    }),
  };

  const substancesColumn = {
    title: (
      <span className="text-[10px]">
        {t('treatment-search|table.active_substances_with_groups')}
      </span>
    ),
    key: 'active_substances',
    render: (_, record) => <ActiveSubstancesCell record={record} />,
    width: 220,
    onHeaderCell: () => ({
      style: {
        height: 'auto',
        verticalAlign: 'middle',
        padding: '8px',
      },
    }),
  };

  const interventionPeriodColumn = {
    title: t('treatment-search|table.Intervention period'),
    key: 'intervention_period',
    render: (_, record) => (
      <TruncatedText
        text={
          record?.intervention_period?.display_name ||
          record?.intervention_period?.name
        }
        maxLength={40}
      />
    ),
    width: 60,
    onHeaderCell: () => ({
      style: {
        height: 'auto',
        verticalAlign: 'middle',
        padding: '8px',
      },
    }),
  };

  const rainDelayColumn = {
    title: t('treatment-search|table.rain_delay_header'),
    key: 'delay_before_rain',
    render: (_, record) => <SimpleTextCell text={record?.delay_before_rain_abbr} />,
    width: 60,
    onHeaderCell: () => ({
      style: {
        height: 'auto',
        verticalAlign: 'middle',
        padding: '8px',
      },
    }),
  };

  const dsColumn = {
    title: t('treatment-search|table.Security delay'),
    key: 'security_delays_combined',
    render: (_, record) => (
      <CombinedDelayCell text={record?.security_delays_combined_abbr} />
    ),
    width: 60,
  };

  const daarColumn = {
    title: t('treatment-search|table.DAAR'),
    key: 'delays_before_harvest_combined',
    render: (_, record) => (
      <CombinedDelayCell text={record?.delays_before_harvest_combined_abbr} />
    ),
    sorter: false,
    width: 60,
  };

  const activityTypesColumn = {
    title: getActivityTypesTitle(),
    key: 'activity_types',

    render: (_, record) => (
      <ActivityTypesCell treatmentProducts={record.treatment_products} />
    ),
    width: 60,
  };

  const weedStageColumn = {
    title: (
      <span className="text-[10px]">
        {t('treatment-search|table.weed_stage')}
      </span>
    ),
    key: 'weed_stage',

    render: (_, record) => (
      <SimpleTextCell
        text={record?.weed_stage?.display_label || record?.weed_stage?.label}
      />
    ),
    width: 80,
  };

  const weedGroupChildColumns = WEED_GROUPS.map((group) => ({
    title: (
      <span className="text-[10px]">
        {t(`treatment-search|${group.labelKey}`)}
      </span>
    ),
    key: `weed_group_${group.key}`,
    className: 'vertical-cell',
    width: 40,
    align: 'center',
    render: (_, record) => {
      const hasGroup = record.treatment_enemies?.some(
        (te) =>
          te.enemy?.weed_group?.label &&
          group.matchLabels.includes(te.enemy.weed_group.label)
      );
      return hasGroup ? <span className="font-bold">X</span> : <span>-</span>;
    },
  }));

  const weedGroupsHeaderColumn = {
    title: (
      <span className="text-[10px]">
        {t('treatment-search|table.weed_groups_header')}
      </span>
    ),
    children: weedGroupChildColumns,
  };

  const healthRiskColumns = {
    title: <div className="py-[10px]">{t('treatment-search|table.risks_on_health')}<Tooltip title={t('treatment-search|riskAssessment.healthRiskSuperscriptTooltip')}><sup className="text-[#105235] cursor-help ml-1" tabIndex={0}>1</sup></Tooltip></div>,

    children: [
      {
        title: <div className="vertical-header-text">{t('treatment-search|table.IRS')}</div>,
        key: 'health_risk_index',
        className: 'vertical-cell',
        render: (_, record) => (
          <RiskIndexDisplay
            value={record?.health_risk_index}
            isExpert={record?.health_risk_index_is_expert}
            variant="badge"
          />
        ),
        width: 32,
      },
      {
        title: <div className="vertical-header-text">{t('treatment-search|table.acute_toxicity')}</div>,
        key: 'acute_toxicity',
        className: 'vertical-cell',
        render: (_, record) => (
          <RiskSymbolCell
            symbolData={record.symbols?.health?.acute_toxicity_symbol}
            alt="Acute toxicity"
          />
        ),
        width: 32,
      },
      {
        title: <div className="vertical-header-text">{t('treatment-search|table.long_term_effects')}</div>,
        key: 'long_term_effects',
        className: 'vertical-cell',
        render: (_, record) => (
          <RiskSymbolCell
            symbolData={record.symbols?.health?.long_term_effect_symbol}
            alt="Long term effects"
          />
        ),
        width: 32,
      },
    ],
  };

  const environmentRiskColumns = {
    title: <div className="py-[10px]">{t('treatment-search|table.risks_on_environment')}<Tooltip title={t('treatment-search|riskAssessment.environmentRiskSuperscriptTooltip')}><sup className="text-[#105235] cursor-help ml-1" tabIndex={0}>2</sup></Tooltip></div>,

    children: [
      {
        title: <div className="vertical-header-text">{t('treatment-search|table.IRE')}</div>,
        key: 'environment_risk_index',
        className: 'vertical-cell',
        render: (_, record) => (
          <RiskIndexDisplay
            value={record?.environment_risk_index}
            isExpert={record?.environment_risk_index_is_expert}
            variant="badge"
          />
        ),
        width: 36,
      },
      {
        title: <div className="vertical-header-text">{t('treatment-search|table.aquatic_organisms')}</div>,
        key: 'fish_daphnia',
        className: 'vertical-cell',
        render: (_, record) => (
          <RiskSymbolCell
            symbolData={record.symbols?.environment?.fishes_symbol}
            alt="Aquatic organisms"
          />
        ),
        width: 32,
      },
      {
        title: <div className="vertical-header-text">{t('treatment-search|table.birds')}</div>,
        key: 'birds',
        className: 'vertical-cell',
        render: (_, record) => (
          <RiskSymbolCell
            symbolData={record.symbols?.environment?.birds_symbol}
            alt="Birds"
          />
        ),
        width: 32,
      },
      {
        title: <div className="vertical-header-text">{t('treatment-search|table.bees')}</div>,
        key: 'bees',
        className: 'vertical-cell',
        render: (_, record) => (
          <RiskSymbolCell
            symbolData={record.symbols?.environment?.bees_symbol}
            alt="Bees"
          />
        ),
        width: 32,
      },
      {
        title: <div className="vertical-header-text">{t('treatment-search|table.persistence')}</div>,
        key: 'persistence',
        className: 'vertical-cell',
        render: (_, record) => (
          <RiskSymbolCell
            symbolData={record.symbols?.environment?.persistence_symbol}
            alt="Persistence"
          />
        ),
        width: 32,
      },
      {
        title: <div className="vertical-header-text">{t('treatment-search|table.mobility')}</div>,
        key: 'mobility',
        className: 'vertical-cell',
        render: (_, record) => (
          <RiskSymbolCell
            symbolData={record.symbols?.environment?.leaching_symbol}
            alt="Mobility"
          />
        ),
        width: 32,
      },
    ],
  };

  const enemiesColumn = {
    title: <div className="vertical-header-text">{t('treatment-search|table.enemies_and_activity_types')}</div>,
    key: 'enemies',
    className: 'vertical-cell',
    render: (_, record) => (
      <EnemiesCell
        treatmentEnemies={record.treatment_enemies}
        filteredEnemyIds={filteredEnemyIds}
        iriisLinks={iriisLinks}
      />
    ),
    width: 60,
  };

  const enemiesAndActivityTypesColumn = {
    title: <div className="vertical-header-text">{t('treatment-search|table.enemies_and_activity_types')}</div>,
    key: 'enemies_activity_types',
    className: 'vertical-cell',
    width: 80,
    align: 'center',
    render: (_, record) => (
      <EnemiesAndActivityTypesCell
        treatmentEnemies={record.treatment_enemies}
        treatmentProducts={record.treatment_products}
        iriisLinks={iriisLinks}
      />
    ),
  };

  const detailsColumn = {
    title: <div className="vertical-header-text">{t('treatment-search|table.treatment_complete')}</div>,
    key: 'details',
    className: 'vertical-cell',
    render: (_, record) => {
      const badges = getTreatmentBadges(record);

      return (
        <div className="flex flex-col items-center gap-1">
          <ActionLinkCell
            href={`/traitement/details/${record.id}?from=${searchMode === 'extraction' ? 'multichoix' : searchMode}${detailQuerySuffix}`}
            target="_blank"
            className="btn btn--primary btn--sm hover:text-inherit"
          >
            {t('treatment-search|table.see')}
          </ActionLinkCell>
          {badges.map((badge) => (
            <Tooltip
              key={badge.key}
              title={t(`treatment-search|${badge.tooltipKey}`)}
            >
              <span
                className="text-[10px] font-bold rounded px-1.5 py-0.5 leading-tight cursor-help whitespace-nowrap"
                style={{ backgroundColor: badge.bgColor, color: badge.textColor }}
              >
                {t(`treatment-search|${badge.labelKey}`)}
              </span>
            </Tooltip>
          ))}
        </div>
      );
    },
    width: 90,
  };

  // --- Assemble columns with conditional visibility ---

  const columns = [
    // Always shown
    productsColumn,
    substancesColumn,

    // Intervention period: shown for non-herbicide always, and for multichoix herbicide
    (!isHerbicide || searchMode === 'multichoix') && interventionPeriodColumn,

    // Rain delay: shown only for herbicide
    isHerbicide && rainDelayColumn,

    // Always shown
    dsColumn,
    daarColumn,

    // Preventif/Curatif: shown only for fongicides (and not herbicides)
    !isHerbicide && isFongicide && activityTypesColumn,

    // Weed stage: herbicide only (not défanants)
    isHerbicide && !isDefanant && weedStageColumn,

    // Weed groups: herbicide only (not défanants)
    isHerbicide && !isDefanant && weedGroupsHeaderColumn,

    // Risk columns always shown
    healthRiskColumns,
    environmentRiskColumns,

    // Multichoix: dynamic culture columns under parent header (only if more than 1 culture selected)
    ...(searchMode === 'multichoix' && cultureColumns.length > 0
      ? [{
          title: <div className="py-[10px]">{t('treatment-search|table.cultures')}</div>,
          children: cultureColumns,
        }]
      : []),

    // Multichoix: dynamic enemy columns under parent header; Rapide: simple enemies column
    ...(searchMode === 'multichoix' && enemyColumns.length > 0
      ? [{
          title: <div className="py-[10px]">{t('treatment-search|table.enemies')}</div>,
          children: enemyColumns,
        }]
      : hasEnemiesFilter
        ? [enemiesColumn]
        : []),

    // Enemies & activity types: rapide only, always shown
    searchMode === 'rapide' && enemiesAndActivityTypesColumn,

    // Details always shown
    detailsColumn,
  ].filter(Boolean);

  return columns;
};

/**
 * Creates extraction table columns (~45 wide columns) for the extraction/Excel preview mode.
 * Same data, much wider table — all fields visible at once.
 */
export const createExtractionTableColumns = (t, searchData, filters, iriisLinks = null, detailQuerySuffix = '') => {
  const treatmentTypeLabel =
    searchData?.selectionLabels?.treatment_type?.[0]?.label?.toLowerCase() ||
    '';
  const isDefanant = treatmentTypeLabel.includes('défanant');
  const isHerbicide =
    treatmentTypeLabel.includes('herbicide') || isDefanant;

  const th = (key) => (
    <span className="text-[10px]">{t(`treatment-search|${key}`)}</span>
  );
  const NA = (
    <span className="text-gray-400 text-xs italic font-light">N/A</span>
  );

  const columns = [
    // 1. Products with doses
    {
      title: th('extraction.products_doses'),
      key: 'ext_products_doses',
      width: 250,
      ellipsis: true,
      render: (_, record) => (
        <ProductWithDoseCell treatmentProducts={record?.treatment_products} />
      ),
    },
    // 2. Approval number
    {
      title: th('extraction.approval_number'),
      key: 'ext_approval_number',
      width: 120,
      render: (_, record) => (
        <ApprovalNumberCell treatmentProducts={record?.treatment_products} />
      ),
    },
    // 3. Organic approval
    {
      title: th('extraction.organic_approval'),
      key: 'ext_organic_approval',
      width: 80,
      align: 'center',
      render: (_, record) => {
        const tp = record?.treatment_products;
        if (!tp?.length) return NA;
        const allBio = tp.every((p) => p?.product?.is_organic_approval);
        return <span className="text-xs">{allBio ? 'Oui' : 'Non'}</span>;
      },
    },
    // 4. Allergens
    {
      title: th('extraction.allergens'),
      key: 'ext_allergens',
      width: 120,
      render: (_, record) => {
        const items = (record?.treatment_products || []).flatMap((tp) =>
          (tp?.product?.allergens || []).filter((a) => a.name)
        );
        return (
          <ItemsWithPopover
            items={items}
            getItemName={(a) => a.name}
            maxVisible={1}
          />
        );
      },
    },
    // 5. Active substances (names only)
    {
      title: th('extraction.active_substances'),
      key: 'ext_active_substances',
      width: 200,
      render: (_, record) => {
        const items = (record?.treatment_products || []).flatMap((tp) =>
          (tp?.product?.active_substances || [])
            .map((asp) => asp?.active_substance)
            .filter((s) => s?.name)
        );
        return (
          <ItemsWithPopover
            items={items}
            getItemName={(s) => s.name}
            maxVisible={1}
          />
        );
      },
    },
    // 6. Pesticide groups
    {
      title: th('extraction.pesticide_groups'),
      key: 'ext_pesticide_groups',
      width: 140,
      render: (_, record) => {
        const items = (record?.treatment_products || []).flatMap((tp) =>
          (tp?.product?.active_substances || []).flatMap((asp) =>
            (asp?.active_substance?.pesticide_group_associations || [])
              .map((assoc) => assoc.pesticide_group)
              .filter((g) => g?.name)
          )
        );
        return (
          <ItemsWithPopover
            items={items}
            getItemName={(g) => g.name}
            maxVisible={1}
          />
        );
      },
    },
    // 7. Resistance risk
    {
      title: th('extraction.resistance_risk'),
      key: 'ext_resistance_risk',
      width: 140,
      render: (_, record) => {
        const items = (record?.treatment_products || []).flatMap((tp) =>
          (tp?.product?.active_substances || []).flatMap((asp) =>
            (asp?.active_substance?.pesticide_group_associations || [])
              .map(
                (assoc) => assoc.pesticide_group?.risk_of_resistance_development
              )
              .filter((r) => r?.name)
          )
        );
        return (
          <ItemsWithPopover
            items={items}
            getItemName={(r) => r.name}
            maxVisible={1}
          />
        );
      },
    },
    // 8. Chemical families
    {
      title: th('extraction.chemical_families'),
      key: 'ext_chemical_families',
      width: 140,
      render: (_, record) => {
        const items = (record?.treatment_products || []).flatMap((tp) =>
          (tp?.product?.active_substances || []).flatMap((asp) =>
            (asp?.active_substance?.pesticide_group_associations || [])
              .map((assoc) => assoc.chemical_family)
              .filter((f) => f?.label)
          )
        );
        return (
          <ItemsWithPopover
            items={items}
            getItemName={(f) => f.label}
            maxVisible={1}
          />
        );
      },
    },
    // 9. Pesticide categories
    {
      title: th('extraction.pesticide_categories'),
      key: 'ext_pesticide_categories',
      width: 140,
      render: (_, record) => {
        const items = (record?.treatment_products || []).flatMap((tp) =>
          (tp?.product?.active_substances || []).flatMap((asp) =>
            (asp?.active_substance?.pesticide_categories || []).filter(
              (c) => c.name
            )
          )
        );
        return (
          <ItemsWithPopover
            items={items}
            getItemName={(c) => c.name}
            maxVisible={1}
          />
        );
      },
    },
    // 10. Activity types (description)
    {
      title: th('extraction.activity_types'),
      key: 'ext_activity_types',
      width: 160,
      render: (_, record) => {
        const items = (record?.treatment_products || []).flatMap((tp) =>
          (tp?.product?.active_substances || []).flatMap((asp) =>
            (asp?.active_substance?.activity_types || []).filter(
              (at) => at.description || at.short_name
            )
          )
        );
        return (
          <ItemsWithPopover
            items={items}
            getItemName={(at) => at.description || at.short_name}
            maxVisible={1}
          />
        );
      },
    },
    // 11. Application type
    {
      title: th('extraction.application_type'),
      key: 'ext_application_type',
      width: 100,
      render: (_, record) => (
        <SimpleTextCell text={record?.application_type?.name} />
      ),
    },
    // 12. Techniques or application places (abbreviation)
    {
      title: th('extraction.techniques_places'),
      key: 'ext_techniques_places',
      width: 120,
      render: (_, record) => (
        <ItemsWithPopover
          items={record?.techniques_or_application_places}
          getItemName={(i) => i.abbreviation || i.name}
          maxVisible={1}
        />
      ),
    },
    // 13. Surfaces to treat
    {
      title: th('extraction.surfaces_to_treat'),
      key: 'ext_surfaces_to_treat',
      width: 120,
      render: (_, record) => (
        <ItemsWithPopover
          items={record?.surfaces_to_treat}
          getItemName={(i) => i.name}
          maxVisible={1}
        />
      ),
    },
    // 14. Application zones
    {
      title: th('extraction.application_zones'),
      key: 'ext_application_zones',
      width: 120,
      render: (_, record) => (
        <ItemsWithPopover
          items={record?.application_zones}
          getItemName={(i) => i.name}
          maxVisible={1}
        />
      ),
    },
    // 15. Application methods
    {
      title: th('extraction.application_methods'),
      key: 'ext_application_methods',
      width: 120,
      render: (_, record) => (
        <ItemsWithPopover
          items={record?.application_methods}
          getItemName={(i) => i.name}
          maxVisible={1}
        />
      ),
    },
    // 16. Soil types (herbicide only, not défanants)
    ...(isHerbicide && !isDefanant
      ? [
          {
            title: th('extraction.soil_types'),
            key: 'ext_soil_types',
            width: 120,
            render: (_, record) => (
              <ItemsWithPopover
                items={record?.soil_type}
                getItemName={(i) => i.name}
                maxVisible={1}
              />
            ),
          },
        ]
      : []),
    // 17. Security delays
    {
      title: th('extraction.security_delays'),
      key: 'ext_security_delays',
      width: 100,
      render: (_, record) => (
        <SimpleTextCell text={record?.security_delays_combined_abbr} />
      ),
    },
    // 18. DAAR
    {
      title: th('extraction.daar'),
      key: 'ext_daar',
      width: 80,
      render: (_, record) => (
        <SimpleTextCell text={record?.delays_before_harvest_combined_abbr} />
      ),
    },
    // 19. Rain delay
    {
      title: th('extraction.rain_delay'),
      key: 'ext_rain_delay',
      width: 100,
      render: (_, record) => {
        const tp = record?.treatment_products;
        if (!tp?.length) return NA;
        if (tp.length === 1) {
          return <SimpleTextCell text={record?.delay_before_rain_abbr} />;
        }
        // Mixture: show per-product if available, or the combined
        return <SimpleTextCell text={record?.delay_before_rain_abbr} />;
      },
    },
    // 20. Agronomic prescription
    {
      title: th('extraction.agronomic_prescription'),
      key: 'ext_agronomic_prescription',
      width: 200,
      render: (_, record) => {
        const comment =
          record?.agronomic_prescription_comments?.comment ||
          record?.agronomic_prescription_comments?.name;
        const text = typeof comment === 'string' ? comment : '';
        return text ? <TruncatedText text={text} maxLength={40} /> : th('extraction.no_agronomic_prescription');
      },
    },
    // 21. IRS (bold)
    {
      title: <div className="vertical-header-text">{t('treatment-search|table.IRS')}</div>,
      key: 'ext_irs',
      className: 'vertical-cell',
      width: 50,
      align: 'center',
      render: (_, record) => (
        <RiskIndexDisplay
          value={record?.health_risk_index}
          isExpert={record?.health_risk_index_is_expert}
          variant="bold"
        />
      ),
    },
    // 22. Acute toxicity
    {
      title: <div className="vertical-header-text">{t('treatment-search|table.acute_toxicity')}</div>,
      key: 'ext_acute_toxicity',
      className: 'vertical-cell',
      width: 50,
      align: 'center',
      render: (_, record) => (
        <RiskSymbolCell
          symbolData={record.symbols?.health?.acute_toxicity_symbol}
          alt="Acute toxicity"
        />
      ),
    },
    // 23. Long term effects
    {
      title: <div className="vertical-header-text">{t('treatment-search|table.long_term_effects')}</div>,
      key: 'ext_long_term_effects',
      className: 'vertical-cell',
      width: 50,
      align: 'center',
      render: (_, record) => (
        <RiskSymbolCell
          symbolData={record.symbols?.health?.long_term_effect_symbol}
          alt="Long term effects"
        />
      ),
    },
    // 24. IRE (bold)
    {
      title: <div className="vertical-header-text">{t('treatment-search|table.IRE')}</div>,
      key: 'ext_ire',
      className: 'vertical-cell',
      width: 50,
      align: 'center',
      render: (_, record) => (
        <RiskIndexDisplay
          value={record?.environment_risk_index}
          isExpert={record?.environment_risk_index_is_expert}
          variant="bold"
        />
      ),
    },
    // 25. Aquatic organisms
    {
      title: <div className="vertical-header-text">{t('treatment-search|table.aquatic_organisms')}</div>,
      key: 'ext_aquatic',
      className: 'vertical-cell',
      width: 50,
      align: 'center',
      render: (_, record) => (
        <RiskSymbolCell
          symbolData={record.symbols?.environment?.fishes_symbol}
          alt="Aquatic organisms"
        />
      ),
    },
    // 26. Birds
    {
      title: <div className="vertical-header-text">{t('treatment-search|table.birds')}</div>,
      key: 'ext_birds',
      className: 'vertical-cell',
      width: 50,
      align: 'center',
      render: (_, record) => (
        <RiskSymbolCell
          symbolData={record.symbols?.environment?.birds_symbol}
          alt="Birds"
        />
      ),
    },
    // 27. Bees
    {
      title: <div className="vertical-header-text">{t('treatment-search|table.bees')}</div>,
      key: 'ext_bees',
      className: 'vertical-cell',
      width: 50,
      align: 'center',
      render: (_, record) => (
        <RiskSymbolCell
          symbolData={record.symbols?.environment?.bees_symbol}
          alt="Bees"
        />
      ),
    },
    // 28. Persistence
    {
      title: <div className="vertical-header-text">{t('treatment-search|table.persistence')}</div>,
      key: 'ext_persistence',
      className: 'vertical-cell',
      width: 50,
      align: 'center',
      render: (_, record) => (
        <RiskSymbolCell
          symbolData={record.symbols?.environment?.persistence_symbol}
          alt="Persistence"
        />
      ),
    },
    // 29. Mobility
    {
      title: <div className="vertical-header-text">{t('treatment-search|table.mobility')}</div>,
      key: 'ext_mobility',
      className: 'vertical-cell',
      width: 50,
      align: 'center',
      render: (_, record) => (
        <RiskSymbolCell
          symbolData={record.symbols?.environment?.leaching_symbol}
          alt="Mobility"
        />
      ),
    },
    // 30. Intervention period
    {
      title: th('extraction.intervention_period'),
      key: 'ext_intervention_period',
      width: 120,
      render: (_, record) => (
        <SimpleTextCell
          text={
            record?.intervention_period?.display_name ||
            record?.intervention_period?.name
          }
        />
      ),
    },
    // 31. Herbicide tolerant variety (herbicide only)
    ...(isHerbicide
      ? [
          {
            title: th('extraction.herbicide_tolerant_variety'),
            key: 'ext_herbicide_tolerant_variety',
            width: 120,
            render: (_, record) => (
              <SimpleTextCell text={record?.herbicide_tolerant_variety?.name} />
            ),
          },
        ]
      : []),
    // 32. Culture stages (herbicide only)
    ...(isHerbicide
      ? [
          {
            title: th('extraction.culture_stages'),
            key: 'ext_culture_stages',
            width: 120,
            render: (_, record) => {
              const stages = record?.culture_stage;
              if (!stages) return NA;
              const items = Array.isArray(stages)
                ? stages.filter((s) => s.name)
                : stages.name
                  ? [stages]
                  : [];
              return (
                <ItemsWithPopover
                  items={items}
                  getItemName={(s) => s.name}
                  maxVisible={1}
                />
              );
            },
          },
        ]
      : []),
  ];

  // 33. Dynamic culture columns (X if treatment covers that culture)
  // Labels may be keyed as 'cultures' (from step flow) or 'treatment_cultures.culture' (from enriched data)
  const selectedCultures =
    searchData?.selectionLabels?.cultures ||
    searchData?.selectionLabels?.['treatment_cultures.culture'] ||
    [];
  if (selectedCultures.length > 0) {
    [...selectedCultures]
      .sort((a, b) => a.label.localeCompare(b.label))
      .forEach((culture) => {
        columns.push({
          title: <div className="vertical-header-text">{culture.label}</div>,
          key: `ext_culture_${culture.value}`,
          className: 'vertical-cell',
          width: 50,
          align: 'center',
          render: (_, record) => {
            const hasCulture = record.treatment_cultures?.some(
              (tc) => tc.culture?.id === culture.value
            );
            return hasCulture ? (
              <span className="font-bold">X</span>
            ) : (
              <span>-</span>
            );
          },
        });
      });
  }

  // 34. Weed stages (herbicide only, not défanants)
  if (isHerbicide && !isDefanant) {
    columns.push({
      title: th('extraction.weed_stages'),
      key: 'ext_weed_stages',
      width: 120,
      render: (_, record) => (
        <SimpleTextCell
          text={record?.weed_stage?.display_label || record?.weed_stage?.label}
        />
      ),
    });
  }

  // 35. Dynamic enemy columns — text-only ratings (no color per spec)
  const selectedEnemies =
    searchData?.selectionLabels?.['treatment_enemies.enemy'] || [];
  if (selectedEnemies.length > 0) {
    [...selectedEnemies]
      .sort((a, b) => a.label.localeCompare(b.label))
      .forEach((enemy) => {
        columns.push({
          title: (
            <Tooltip title={enemy.label}>
              <div className="vertical-header-text">
                <IriisLink url={iriisLinks?.[enemy.value]?.url}>
                  {enemy.label.length > 20 ? `${enemy.label.slice(0, 20)}...` : enemy.label}
                </IriisLink>
              </div>
            </Tooltip>
          ),
          key: `ext_enemy_${enemy.value}`,
          className: 'vertical-cell',
          width: 60,
          render: (_, record) => {
            const treatmentEnemy = record.treatment_enemies?.find(
              (te) => te.enemy?.id === enemy.value
            );
            const rating = treatmentEnemy?.enemy_rating_ARLA;
            if (rating?.name) {
              return <span className="text-xs">{rating.name}</span>;
            }
            return NA;
          },
        });
      });
  }

  // 36-40. Weed groups (herbicide only, not défanants)
  if (isHerbicide && !isDefanant) {
    WEED_GROUPS.forEach((group) => {
      columns.push({
        title: (
          <div className="vertical-header-text">
            {t(`treatment-search|${group.labelKey}`)}
          </div>
        ),
        key: `ext_weed_group_${group.key}`,
        className: 'vertical-cell',
        width: 50,
        align: 'center',
        render: (_, record) => {
          const hasGroup = record.treatment_enemies?.some(
            (te) =>
              te.enemy?.weed_group?.label &&
              group.matchLabels.includes(te.enemy.weed_group.label)
          );
          return hasGroup ? (
            <span className="font-bold">X</span>
          ) : (
            <span>-</span>
          );
        },
      });
    });
  }

  // 41. Conditions of success
  columns.push({
    title: th('extraction.conditions_success'),
    key: 'ext_conditions_success',
    width: 200,
    render: (_, record) => {
      const text = record?.conditions_of_success_and_warnings;
      return text ? <TruncatedText text={text} maxLength={40} /> : NA;
    },
  });

  // 42. PI intervention periods
  columns.push({
    title: th('extraction.pi_intervention_periods'),
    key: 'ext_pi_intervention_periods',
    width: 200,
    render: (_, record) => {
      const text =
        record?.recommended_intervention_period_for_integrated_production;
      return text ? <TruncatedText text={text} maxLength={40} /> : NA;
    },
  });

  // 43. Approval status + (NOUVEAU)
  columns.push({
    title: th('extraction.approval_status'),
    key: 'ext_approval_status',
    width: 120,
    render: (_, record) => (
      <ApprovalStatusCell
        record={record}
        nouveauLabel={t('treatment-search|extraction.nouveau_suffix')}
      />
    ),
  });

  // 44. Treatment number (easy_id)
  columns.push({
    title: th('extraction.treatment_number'),
    key: 'ext_treatment_number',
    width: 80,
    render: (_, record) =>
      record?.easy_id ? <span className="text-xs">{record.easy_id}</span> : NA,
  });

  // 45. Treatment URL
  columns.push({
    title: th('extraction.treatment_url'),
    key: 'ext_treatment_url',
    width: 100,
    render: (_, record) => (
      <ActionLinkCell
        href={`/traitement/details/${record.id}?from=multichoix${detailQuerySuffix}`}
        target="_blank"
        className="btn btn--primary btn--sm hover:text-inherit"
      >
        {t('treatment-search|table.see')}
      </ActionLinkCell>
    ),
  });

  return columns;
};
